import type { AstroSummary, UserDoc, UserScoreProfileDoc } from '@astrally/shared';

/**
 * Seeded demo people. Their astro summaries are hand-set (sign/nakshatra
 * indexes) so matching produces varied grades; the CURRENT user's summary is
 * derived from their real onboarding chart by the engine.
 */

export interface DemoPerson {
  user: UserDoc;
  scores: UserScoreProfileDoc['dimensions'];
  trustLevel: number;
  astro: AstroSummary;
}

const now = '2026-01-01T00:00:00.000Z';

function person(
  uid: string,
  displayName: string,
  bio: string,
  interests: string[],
  intents: UserDoc['intents'],
  astro: Partial<AstroSummary>,
  dims?: Partial<UserScoreProfileDoc['dimensions']>,
): DemoPerson {
  return {
    user: {
      uid,
      displayName,
      bio,
      ageBand: '25-34',
      region: 'in-mh-mumbai',
      city: 'Mumbai',
      languages: ['en', 'hi'],
      intents,
      lifeStage: 'early-career',
      interests,
      // Seeded demo people have already opted in — that is what makes them
      // discoverable. Your own demo account still starts private.
      socialEnabled: true,
      socialConsentAtISO: now,
      discoverable: true,
      recommendationsPaused: false,
      ageConfirmed: true,
      createdAtISO: now,
      lastActiveAtISO: now,
    },
    scores: {
      communicationDirectness: 0.7,
      conversationDepth: 0.65,
      humour: 0.6,
      emotionalOpenness: 0.55,
      socialEnergy: 0.5,
      replyConsistency: 0.7,
      profileCompleteness: 0.9,
      reliability: 0.8,
      ...dims,
    },
    trustLevel: 0.92,
    astro: {
      timeConfidence: 'exact',
      sunSiderealSign: 7,
      sunTropicalSign: 8,
      moonSiderealSigns: [1],
      moonNakshatras: [3],
      lagnaSign: 3,
      mangalDosha: 'no',
      venusTropicalSign: 6,
      marsTropicalSign: 9,
      engineVersion: 'astro-engine@0.1.0',
      ...astro,
    },
  };
}

export const DEMO_PEOPLE: DemoPerson[] = [
  person(
    'demo-meera',
    'Meera',
    'Product designer who sketches temple architecture on weekends.',
    ['technology', 'business', 'design', 'trekking'],
    ['professional', 'friendship', 'astrology-discussion'],
    { moonSiderealSigns: [1], moonNakshatras: [3], sunSiderealSign: 9 },
    { communicationDirectness: 0.8, conversationDepth: 0.75 },
  ),
  person(
    'demo-arjun',
    'Arjun',
    'Early-stage founder. Chai over coffee, always.',
    ['startups', 'technology', 'business', 'cricket'],
    ['founder', 'professional', 'friendship'],
    { moonSiderealSigns: [4], moonNakshatras: [9], sunSiderealSign: 0 },
    { communicationDirectness: 0.9, socialEnergy: 0.8 },
  ),
  person(
    'demo-kavya',
    'Kavya',
    'Clinical psychologist exploring Jyotish seriously, not superstitiously.',
    ['psychology', 'astrology', 'reading', 'music'],
    ['friendship', 'astrology-discussion', 'study'],
    { moonSiderealSigns: [11], moonNakshatras: [25], sunSiderealSign: 3, timeConfidence: 'approximate' },
    { emotionalOpenness: 0.85, conversationDepth: 0.9 },
  ),
  person(
    'demo-rohan',
    'Rohan',
    'Data scientist, marathon runner, amateur tabla player.',
    ['technology', 'music', 'running', 'films'],
    ['romance', 'friendship'],
    { moonSiderealSigns: [7], moonNakshatras: [16], sunSiderealSign: 5, mangalDosha: 'yes' },
    { humour: 0.8, replyConsistency: 0.85 },
  ),
  person(
    'demo-ananya',
    'Ananya',
    'Writes about cities, food and belonging. New to Mumbai.',
    ['writing', 'food', 'cities', 'music'],
    ['friendship', 'creative', 'local-activities'],
    { moonSiderealSigns: [2], moonNakshatras: [6], sunSiderealSign: 10, timeConfidence: 'unknown', lagnaSign: undefined },
    { emotionalOpenness: 0.7, socialEnergy: 0.75 },
  ),
];

export const DEMO_SELF_UID = 'demo-you';
